// SearchBar.jsx
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClickAwayListener,
  Paper,
  IconButton,
  Popper,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useDisableMobileTextAutofocus from '../../hooks/useDisableMobileTextAutofocus';

const SearchBar = ({
  mobileMode = 'default',
  mobileOpen = false,
  onMobileOpenChange,
  initialValue = '',
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [open, setOpen] = useState(false);

  const anchorRef = useRef(null);
  const inputRef = useRef(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();

  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));   // md+ : barre visible
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm')); // xs : mobile avec navbar du bas
  const mobileSearchOpen = isSmDown && (mobileMode === 'panel' ? mobileOpen : open);

  useDisableMobileTextAutofocus(mobileSearchOpen);

  useEffect(() => {
    setSearchTerm(initialValue);
  }, [initialValue]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchTerm) {
      navigate(`/search/${searchTerm}`);
      setSearchTerm('');
      if (isSmDown && mobileMode === 'panel') {
        onMobileOpenChange?.(false);
      } else {
        setOpen(false);
      }
    }
  };

  const SearchField = (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      sx={{
        borderRadius: '6px',
        border: '1.3px solid #fff',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        pl: 2,
        boxShadow: 'none',
        background: '#18181b',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <input
        ref={inputRef}
        className="search-bar"
        placeholder={t("search.placeholder")}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ flex: 1, minWidth: 0, fontSize: 16 }}
      />
      <IconButton type="submit" sx={{ p: '7px', color: '#fff' }}>
        <Search />
      </IconButton>
    </Paper>
  );

  // 🌐 Mode desktop : SearchBar inline dans la navbar
  if (isMdUp) {
    return (
      <Box sx={{ width: { xs: '100%', sm: '70%', md: '60%', lg: '50%' } }}>
        {SearchField}
      </Box>
    );
  }

  // 📱 Mode mobile (xs) : icône dans la navbar du bas, popper "fixe" sous la navbar du haut
  if (isSmDown) {
    if (mobileMode === 'panel') {
      return <Box sx={{ width: '100%', maxWidth: '100%' }}>{SearchField}</Box>;
    }

    return (
      <>
        <IconButton
          ref={anchorRef}
          onClick={() => {
            if (mobileMode === 'trigger') {
              onMobileOpenChange?.(!mobileOpen);
              return;
            }
            setOpen((o) => !o);
          }}
          sx={{
            p: '0px !important',
          }}
          aria-label={t("search.ariaLabel")}
        >
          {/* 👇 Conteneur relatif pour placer les cercles sous la loupe */}
          <Box
            sx={{
              position: 'relative',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Petit rond blanc dans le "trou" de la loupe */}
            {(mobileMode === 'trigger' ? mobileOpen : open) && (
              <Box
                sx={{
                  position: 'absolute',
                  width: 6.3,
                  height: 6.3,
                  borderRadius: '999px',
                  backgroundColor: '#ffdb42',
                  top: '41.56%',   // 👈 tu peux affiner top / left ici
                  left: '41.02%',  // 👈 pour le placer pile dans le "trou"
                  transform: 'translate(-50%, -50%)',
                }}
              />
            )}

            {/* Icône loupe au-dessus */}
            <Search sx={{ fontSize: 26, color: '#ffffff', position: 'relative', zIndex: 1 }} />
          </Box>
        </IconButton>
      </>
    );
  }

  // 🧊 Mode "petite tablette" (entre sm et md) : on garde le Popper ancré à l'IconButton
  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        ref={anchorRef}
        onClick={() => setOpen((o) => !o)}
        sx={{ color: '#fff' }}
        aria-label={t("search.ariaLabel")}
      >
        <Search />
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 31 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box sx={{ mt: 1, width: 260, p: 0 }}>
            {SearchField}
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default SearchBar;
